// Sugalarm — Cycle 361: the fired-reminder auto-dismiss deadline.
//
// Pure, so the invariant below is unit-testable without React (the cycle-351
// `alarmPreview.ts` precedent — expiry logic lives in lib/, the hook is a thin
// wrapper). `useReminderOverlay` is the ONLY consumer; do not re-derive the
// 10-minute window or the comparison anywhere else.
//
// THE INVARIANT: a fired reminder's takeover expires on a WALL-CLOCK DEADLINE
// evaluated at READ time — never on a `setTimeout`. iOS WKWebView and Android
// WebView suspend JS timers while backgrounded and fire them arbitrarily late
// on resume; that is the mechanism behind the cycle-351 app-open false-high,
// and it is why cycle 351 rebuilt alarm-preview expiry this same way. A
// suspendable timer here would recreate the cycle-361 bug it exists to remove
// (a 10:00 AM reminder surfacing mid-afternoon).
//
// The deadline is also deliberately independent of whether the overlay is
// currently VISIBLE: a reminder withheld while an alarm sounds still ages out
// on schedule, so it can never resurface once the alarm clears.

/** Operator directive (cycle 361): the takeover clears itself 10 minutes after
 *  the fire arrived if Close was never pressed. */
export const REMINDER_AUTO_DISMISS_MS = 10 * 60_000;

/**
 * True once a reminder that arrived at `firedAtMs` has outlived the auto-dismiss
 * window as of `nowMs`.
 *
 * Boundary: expiry is inclusive at exactly `REMINDER_AUTO_DISMISS_MS` elapsed.
 * A non-finite / missing `firedAtMs` is treated as EXPIRED — a reminder whose
 * arrival instant we cannot trust must not become an un-clearable latch (the
 * failure this cycle exists to eliminate); the reminder still reached the
 * household via the server-side push fan-out, which this never touches.
 * A clock that jumps backwards yields a negative elapsed and reads as
 * not-expired, so the overlay stays until the clock recovers.
 */
export function isReminderExpired(
  firedAtMs: number,
  nowMs: number,
  windowMs: number = REMINDER_AUTO_DISMISS_MS,
): boolean {
  if (!Number.isFinite(firedAtMs)) return true;
  return nowMs - firedAtMs >= windowMs;
}
